import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Award,
  Check,
  Copy,
  GraduationCap,
  KeyRound,
  Presentation,
  Settings2,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import useCtx from "../hooks/useCts";
import TopNavigator from "../components/TopNavigator";
import Footer from "../components/Footer";
import Teachers from "../components/Teachers";
import Students from "../components/Students";
import Settings from "../components/Settings";
import Groups from "../components/Groups";
import CertificateGenerator from "../components/CertificateGenerator";
import Skyline from "../assets/london-skyline.jpg";

type ManagementTab =
  | "teachers"
  | "students"
  | "settings"
  | "groups"
  | "certificates";

const isManagementTab = (value: unknown): value is ManagementTab =>
  ["teachers", "students", "settings", "groups", "certificates"].includes(
    String(value),
  );

const ManagmentScreen: React.FC = () => {
  const ctx = useCtx();
  const location = useLocation();
  const requestedTab = isManagementTab(
    (location.state as { activeTab?: unknown } | null)?.activeTab,
  )
    ? (location.state as { activeTab: ManagementTab }).activeTab
    : null;
  const [activeTab, setActiveTab] = useState<ManagementTab>(
    requestedTab ?? "groups",
  );
  const [certificateSelectorSignal, setCertificateSelectorSignal] = useState(0);
  const [copiedToken, setCopiedToken] = useState(false);
  const tabContentRef = useRef<HTMLDivElement | null>(null);

  const userClient = useMemo(
    () => ctx.currentClient,
    [ctx.currentClient],
  );

  const school = useMemo(
    () => ctx.schools.find((item) => item.id === userClient?.schoolId) ?? null,
    [ctx.schools, userClient?.schoolId],
  );

  const teacherCount = userClient?.teachers?.length ?? 0;
  const studentCount = useMemo(
    () =>
      ctx.students.filter(
        (student) => student.schoolToken === userClient?.token,
      ).length,
    [ctx.students, userClient?.token],
  );

  const copyTokenToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedToken(true);
        setTimeout(() => setCopiedToken(false), 5000);
      })
      .catch((err) => {
        console.error("Błąd kopiowania: ", err);
      });
  };

  useEffect(() => {
    if (requestedTab) {
      setActiveTab(requestedTab);
      if (requestedTab === "certificates") {
        setCertificateSelectorSignal((currentValue) => currentValue + 1);
      }
    }
  }, [requestedTab]);

  const handleTabClick = (nextTab: ManagementTab) => {
    setActiveTab(nextTab);
    if (nextTab === "certificates") {
      setCertificateSelectorSignal((currentValue) => currentValue + 1);
    }

    window.requestAnimationFrame(() => {
      tabContentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const tabs = [
    {
      key: "certificates",
      label: "Certyfikaty",
      description: "Generator",
      Icon: Award,
      accentClassName: "bg-amber-500",
      iconClassName: "bg-amber-50 text-amber-700",
    },
    {
      key: "groups",
      label: "Grupy",
      description: "Zarządzaj grupami.",
      Icon: Presentation,
      accentClassName: "bg-violet-500",
      iconClassName: "bg-violet-50 text-violet-700",
    },
    {
      key: "teachers",
      label: "Lektorzy",
      description: "Zarządzaj dostępami lektorów",
      Icon: UsersRound,
      accentClassName: "bg-sky-500",
      iconClassName: "bg-sky-50 text-sky-700",
    },
    {
      key: "students",
      label: "Uczniowie",
      description: "Wyniki testów",
      Icon: GraduationCap,
      accentClassName: "bg-emerald-500",
      iconClassName: "bg-emerald-50 text-emerald-700",
    },
    {
      key: "settings",
      label: "Ustawienia",
      description: "Token, testy i logo szkoły",
      Icon: Settings2,
      accentClassName: "bg-orange-500",
      iconClassName: "bg-orange-50 text-[#c55f0d]",
    },
  ] as const;

  return (
    <>
      <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#f7f2ea]">
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-cover bg-center bg-no-repeat opacity-40"
          style={{ backgroundImage: `url(${Skyline})` }}
        />
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-[linear-gradient(180deg,rgba(255,250,245,0.82),rgba(255,255,255,0.66)_24%,rgba(248,250,252,0.5))]"
        />
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-[radial-gradient(circle_at_top_left,rgba(246,133,31,0.18),transparent_50%),radial-gradient(circle_at_88%_18%,rgba(44,150,233,0.24),transparent_54%),linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.24))]"
        />

        <div className="relative z-10 flex flex-1 flex-col">
          <TopNavigator />

          <main className="mx-auto w-full max-w-7xl flex-1 px-4 pb-16 pt-6 sm:px-6 lg:px-8">
            <section className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_360px] lg:items-start">
              <div className="px-1 pt-4 sm:pt-8 lg:pt-12">
                <h1 className="max-w-3xl bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text pb-1 text-3xl font-semibold leading-[1.12] tracking-[-0.04em] text-transparent sm:text-4xl lg:text-5xl">
                  Zarządzaj ustawieniami konta.
                </h1>
                <h2 className="mt-5 max-w-3xl font-medium leading-8 tracking-[-0.03em] text-slate-700 sm:text-2xl sm:leading-9 lg:text-[1.9rem]">
                  Dodaj lektorów, pobierz testy poziomujące, sprawdź wyniki
                  testów swoich uczniów.
                </h2>
                <button
                  type="button"
                  onClick={() => handleTabClick("certificates")}
                  className="mt-6 inline-flex items-center gap-3 rounded-lg border border-[#1b2430] bg-[#1b2430] px-5 py-3 text-left text-white shadow-[0_18px_42px_rgba(27,36,48,0.22)] transition hover:-translate-y-0.5 hover:bg-[#243244]"
                >
                  <Award size={18} strokeWidth={2.2} className="text-white" />
                  <span className="text-base font-semibold tracking-[-0.02em] text-white">
                    Generator certyfikatów
                  </span>
                  <span className="rounded-md bg-[#ffbc08] px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-black">
                    nowość
                  </span>
                </button>
              </div>

              <aside className="hidden w-full sm:block lg:max-w-[360px] lg:justify-self-end">
                <div className="rounded-[28px] border border-white/50 bg-white/10 p-5 shadow-[0_20px_60px_rgba(148,163,184,0.18)] backdrop-blur-sm sm:p-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[18px] border border-white/30 shadow-[0_14px_30px_rgba(148,163,184,0.36)]">
                      <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-black/5 bg-white/50 text-slate-700">
                        <ShieldCheck
                          size={18}
                          strokeWidth={2.2}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                        Strefa Admina
                      </p>
                      <p className="mt-1 text-2xl font-semibold tracking-[-0.03em] text-slate-900">
                        Panel zarządzania
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="rounded-2xl border border-white/50 bg-white/10 p-4">
                        <div className="flex items-center gap-3 text-slate-500">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/30 shadow-[0_10px_22px_rgba(148,163,184,0.24)]">
                            <div className="flex h-6 w-6 items-center justify-center rounded-lg border border-black/5 bg-white/50 text-slate-700">
                              <UsersRound
                                size={14}
                                strokeWidth={2.2}
                                aria-hidden="true"
                              />
                            </div>
                          </div>
                          <p className="text-sm">Lektorzy</p>
                        </div>
                        <p className="mt-2 text-2xl font-semibold text-slate-900">
                          {teacherCount}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-white/50 bg-white/10 p-4">
                        <div className="flex items-center gap-3 text-slate-500">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/30 shadow-[0_10px_22px_rgba(148,163,184,0.24)]">
                            <div className="flex h-6 w-6 items-center justify-center rounded-lg border border-black/5 bg-white/50 text-slate-700">
                              <GraduationCap
                                size={14}
                                strokeWidth={2.2}
                                aria-hidden="true"
                              />
                            </div>
                          </div>
                          <p className="text-sm">Uczniowie</p>
                        </div>
                        <p className="mt-2 text-2xl font-semibold text-slate-900">
                          {studentCount}
                        </p>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/50 bg-white/10 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 text-slate-500">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/30 shadow-[0_10px_22px_rgba(148,163,184,0.24)]">
                            <div className="flex h-6 w-6 items-center justify-center rounded-lg border border-black/5 bg-white/50 text-slate-700">
                              <KeyRound
                                size={14}
                                strokeWidth={2.2}
                                aria-hidden="true"
                              />
                            </div>
                          </div>
                          <p className="text-sm">Token szkoły</p>
                        </div>
                        {userClient?.token && (
                          <button
                            type="button"
                            onClick={() =>
                              copyTokenToClipboard(userClient.token)
                            }
                            className="rounded-full border border-slate-200 bg-white/50 p-2 transition hover:bg-slate-50"
                            title={copiedToken ? "Skopiowano!" : "Kopiuj token"}
                          >
                            {copiedToken ? (
                              <Check
                                size={16}
                                strokeWidth={2.4}
                                className="text-green-500"
                              />
                            ) : (
                              <Copy size={16} strokeWidth={2.2} />
                            )}
                          </button>
                        )}
                      </div>
                      <p className="mt-2 break-all text-base font-medium text-slate-800">
                        {userClient?.token || "Brak tokenu"}
                      </p>
                    </div>
                  </div>
                </div>
              </aside>
            </section>

            <section className="mt-10">
              <div className="rounded-[28px] border  border-white/50  p-2 shadow-[0_20px_60px_rgba(148,163,184,0.28)] backdrop-blur-sm">
                <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
                  {tabs.map(
                    ({
                      key,
                      label,
                      description,
                      accentClassName,
                    }) => {
                      const active = activeTab === key;

                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => handleTabClick(key)}
                          className={`rounded-[22px] border p-3 text-left transition sm:p-4 ${
                            active
                              ? "border-white/50 bg-sky-50/80 shadow-[0_18px_40px_rgba(148,163,184,0.12)]"
                              : "border-transparent bg-transparent hover:border-white/30 hover:bg-white/10 hover:shadow-[0_12px_28px_rgba(148,163,184,0.16)]"
                          }`}
                        >
                          <div className="min-w-0">
                            <p className="bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text text-sm font-semibold text-transparent sm:text-base">
                              {label}
                            </p>
                            <p
                              className={`mt-2 hidden items-start gap-2 text-sm sm:flex ${
                                active ? "text-slate-700" : "text-slate-600"
                              }`}
                            >
                              <span
                                className={`mt-[0.45rem] h-2 w-2 shrink-0 rounded-full ${
                                  active ? accentClassName : "bg-slate-400"
                                }`}
                                aria-hidden="true"
                              />
                              <span>{description}</span>
                            </p>
                          </div>
                        </button>
                      );
                    },
                  )}
                </div>
              </div>

              <div ref={tabContentRef} className="mt-4 scroll-mt-6">
                {activeTab === "groups" && <Groups />}
                {activeTab === "teachers" && <Teachers />}
                {activeTab === "students" && <Students />}
                {activeTab === "certificates" && (
                  <CertificateGenerator
                    selectorHighlightSignal={certificateSelectorSignal}
                  />
                )}
                {activeTab === "settings" && <Settings />}
              </div>
            </section>
          </main>
        </div>
        {activeTab !== "certificates" && <Footer />}
      </div>
    </>
  );
};

export default ManagmentScreen;
